import React, { useRef, useEffect, useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import Hls from 'hls.js';
import { QRCodeSVG } from 'qrcode.react';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

// Get the API URL from environment or use the host IP
const API_URL = process.env.REACT_APP_API_URL || 'https://45.225.80.187:8010';

const TestResults = ({ results }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [videoError, setVideoError] = useState(null);
  const [isHlsLoading, setIsHlsLoading] = useState(false);
  const [isInitialLoadDelay, setIsInitialLoadDelay] = useState(true);
  
  // Função para inicializar o player HLS
  const initializeHlsPlayer = () => {
    if (!results.hls_url || !videoRef.current) return;
    
    // Ensure we use the backend API URL and not the frontend origin
    let hlsUrl;
    if (results.hls_url.startsWith('http')) {
      hlsUrl = results.hls_url;
    } else {
      // If it's a relative URL, prepend the API_URL
      hlsUrl = `${API_URL}${results.hls_url}`;
    }
    
    console.log('Initializing HLS player with URL:', hlsUrl);
    setIsHlsLoading(true);
    setVideoError(null);
    
    if (Hls.isSupported()) {
      // Limpar qualquer instância anterior do HLS
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      // Criar nova instância
      const hls = new Hls({
        debug: true, // Enable debug to see more information in console
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90
      });
      
      hlsRef.current = hls;
      
      // Configurar listeners de eventos
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch(data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Network error:', data.details);
              setVideoError(`Network error: ${data.details}`);
              
              // Tentar reconectar automaticamente
              setTimeout(() => {
                console.log('Attempting to recover from network error...');
                hls.startLoad();
              }, 3000);
              break;
              
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Media error:', data.details);
              setVideoError(`Media error: ${data.details}`);
              
              // Tentar recuperar de erro de mídia
              hls.recoverMediaError();
              break;
              
            default:
              console.error('Fatal error:', data.type, data.details);
              setVideoError(`Fatal error: ${data.details}`);
              hls.destroy();
              break;
          }
        }
      });
      
      // Carregar a fonte e anexar ao vídeo
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest parsed, attempting to play');
        setIsHlsLoading(false);
        
        // Delay the initial play attempt to give the stream more time to be ready
        const playVideo = () => {
          videoRef.current.play().catch(error => {
            console.log('Autoplay prevented:', error);
            setVideoError('Autoplay prevented. Click play to start the stream.');
          });
          setIsInitialLoadDelay(false);
        };
        
        if (isInitialLoadDelay) {
          console.log('Adding initial delay of 3 seconds before playing...');
          setTimeout(playVideo, 3000);
        } else {
          playVideo();
        }
      });
      
      hls.on(Hls.Events.FRAG_LOADED, () => {
        if (videoError) {
          setVideoError(null);
        }
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Suporte nativo para HLS (Safari)
      console.log('Using native HLS support');
      videoRef.current.src = hlsUrl;
      videoRef.current.addEventListener('loadedmetadata', () => {
        setIsHlsLoading(false);
        
        // Delay the initial play attempt for Safari as well
        const playVideo = () => {
          videoRef.current.play().catch(error => {
            console.log('Autoplay prevented:', error);
            setVideoError('Autoplay prevented. Click play to start the stream.');
          });
          setIsInitialLoadDelay(false);
        };
        
        if (isInitialLoadDelay) {
          console.log('Adding initial delay of 3 seconds before playing (Safari)...');
          setTimeout(playVideo, 3000);
        } else {
          playVideo();
        }
      });
      
      videoRef.current.addEventListener('error', (e) => {
        console.error('Video error:', videoRef.current.error);
        setVideoError(`Error loading video: ${videoRef.current.error?.message || 'Unknown error'}`);
      });
    } else {
      console.error('HLS not supported by this browser and no native support');
      setVideoError('HLS playback not supported by this browser');
      setIsHlsLoading(false);
    }
  };
  
  // Inicializar o player quando os resultados mudarem
  useEffect(() => {
    if (results.hls_url) {
      // Set initial delay state to true whenever results change
      setIsInitialLoadDelay(true);
      initializeHlsPlayer();
    }
    
    // Cleanup
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [results.hls_url]);
  
  // Função para reiniciar o stream
  const handleRestartStream = () => {
    console.log('Restarting stream...');
    setIsInitialLoadDelay(false); // No initial delay for manual restart
    initializeHlsPlayer();
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'RTSP Test Results',
        text: 'Check out these RTSP stream test results',
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback for browsers that don't support Web Share API
      alert('Share functionality not supported by your browser.');
    }
  };
  
  const handleDownload = () => {
    // Create a JSON blob and trigger download
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(results, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "rtsp_test_results.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };
  
  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            Stream Analysis Results
          </Typography>
          <Box>
            <Button startIcon={<PrintIcon />} onClick={handlePrint} sx={{ mr: 1 }}>
              Print
            </Button>
            <Button startIcon={<ShareIcon />} onClick={handleShare} sx={{ mr: 1 }}>
              Share
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
              Download
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={4}>
          {/* Video Player (if HLS available) */}
          {results.hls_url && (
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Live Preview
                </Typography>
                <Button 
                  startIcon={<RefreshIcon />} 
                  onClick={handleRestartStream}
                  variant="outlined"
                  size="small"
                >
                  Restart Stream
                </Button>
              </Box>
              
              {videoError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {videoError}
                </Alert>
              )}
              
              {isHlsLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Loading stream, please wait...
                </Alert>
              )}
              
              <Box sx={{ width: '100%', aspectRatio: '16/9', bgcolor: 'black', position: 'relative' }}>
                <video 
                  ref={videoRef}
                  controls
                  style={{ width: '100%', height: '100%' }}
                  poster="/video-placeholder.jpg"
                >
                  Your browser does not support the video tag.
                </video>
                
                {(isHlsLoading || isInitialLoadDelay) && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexDirection: 'column',
                      bgcolor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      zIndex: 1
                    }}
                  >
                    <CircularProgress color="secondary" sx={{ mb: 2 }} />
                    <Typography>
                      {isInitialLoadDelay ? 'Preparing stream...' : 'Loading stream...'}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Note: If the stream does not start automatically, click the play button or use the "Restart Stream" button.
              </Typography>
            </Grid>
          )}
          
          {/* Technical Info */}
          <Grid item xs={12} md={results.hls_url ? 6 : 12}>
            <Typography variant="h6" gutterBottom>
              Technical Information
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Status
                    </TableCell>
                    <TableCell>Success</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Video Codec
                    </TableCell>
                    <TableCell>{results.video_codec || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Audio Codec
                    </TableCell>
                    <TableCell>{results.audio_codec || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Resolution
                    </TableCell>
                    <TableCell>{results.resolution || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Frame Rate
                    </TableCell>
                    <TableCell>{results.framerate ? `${results.framerate} fps` : 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Video Bitrate
                    </TableCell>
                    <TableCell>{results.video_bitrate ? `${results.video_bitrate} kbps` : 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Audio Bitrate
                    </TableCell>
                    <TableCell>{results.audio_bitrate ? `${results.audio_bitrate} kbps` : 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Processing Time
                    </TableCell>
                    <TableCell>{results.processing_time ? `${results.processing_time.toFixed(2)} seconds` : 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      RTSP URL
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{results.url || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* QR Code for sharing */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Scan to share these results
                </Typography>
                <Paper elevation={2} sx={{ p: 2, display: 'inline-block' }}>
                  <QRCodeSVG value={window.location.href} size={128} />
                </Paper>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TestResults;